import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { buildKakaoAuthorizeUrl } from '@/lib/socialAuth';
import { adminKakaoMemoApi } from '@/api/adminKakaoMemoApi';
import { Spinner } from '@/components/ui/spinner';
const API_BASE_URL = `${BASE}/api`;

// 카카오톡 "나에게 보내기" 관리자 알림 최초 연동 흐름 - 백엔드가 로그인용 redirect_uri를
// 재사용하며 발급하는 state의 접두사(KakaoMemoNotificationService.STATE_PREFIX와 동일해야
// 함). 일반 로그인은 state를 전혀 쓰지 않으므로 이 분기와 충돌하지 않는다.
const KAKAO_MEMO_STATE_PREFIX = 'kakao_memo_';

const processedKakaoCodes = new Set<string>();

const KAKAO_EMAIL_REQUIRED_CODES = new Set([
  'KAKAO_EMAIL_REQUIRED',
  'KAKAO_EMAIL_MISSING',
  'KAKAO_EMAIL_CONSENT_REQUIRED',
  'SOCIAL_EMAIL_REQUIRED',
  'SOCIAL_EMAIL_MISSING',
  'OAUTH_EMAIL_REQUIRED',
  'OAUTH_EMAIL_MISSING',
  'EMAIL_REQUIRED',
  'EMAIL_NOT_PROVIDED',
]);

type OAuthCallbackError = {
  code?: string;
  status?: number;
  message?: string;
  data?: any;
};

type KakaoEmailRequiredPayload = {
  kakaoId: string;
  name?: string;
};

const normalizeCode = (code?: string): string => {
  return (code || '').trim().toUpperCase();
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getPendingSignupStorageKey = (code: string): string => {
  return `oauth:kakao:email-required:${code}`;
};

const extractErrorMeta = (error: OAuthCallbackError) => {
  const responseData = error.data;
  // B/E 표준 에러 응답 errorType 우선 확인
  const responseCode = normalizeCode(
    responseData?.errorType || responseData?.errorCode || responseData?.error || responseData?.code
  );
  const nestedCode = normalizeCode(responseData?.data?.error || responseData?.data?.code);
  return {
    code: responseCode || nestedCode || normalizeCode(error.code),
    message: responseData?.message || error.message || '',
    data: (responseData?.data && typeof responseData.data === 'object') ? responseData.data : undefined,
  };
};

const isKakaoEmailRequiredError = (error: OAuthCallbackError): boolean => {
  const meta = extractErrorMeta(error);
  if (KAKAO_EMAIL_REQUIRED_CODES.has(meta.code)) {
    return true;
  }

  const message = `${meta.message}`.toLowerCase();
  const hasEmailKeyword = /email|이메일/.test(message);
  const hasRequiredKeyword = /required|missing|not provided|필요|누락|동의|받지 못/.test(message);
  const hasKakaoKeyword = /kakao|카카오/.test(message);

  if (error.status === 422 && hasEmailKeyword && hasRequiredKeyword && hasKakaoKeyword) {
    return true;
  }

  return false;
};

const extractKakaoEmailRequiredPayload = (error: OAuthCallbackError): KakaoEmailRequiredPayload | null => {
  const meta = extractErrorMeta(error);
  const kakaoIdRaw = meta.data?.kakaoId;

  if (kakaoIdRaw === undefined || kakaoIdRaw === null || `${kakaoIdRaw}`.trim() === '') {
    return null;
  }

  const nameValue = typeof meta.data?.name === 'string' ? meta.data.name.trim() : '';

  return {
    kakaoId: `${kakaoIdRaw}`.trim(),
    name: nameValue || undefined,
  };
};

const readPendingSignupPayload = (code: string): KakaoEmailRequiredPayload | null => {
  const storageKey = getPendingSignupStorageKey(code);

  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.kakaoId) {
      return null;
    }

    return {
      kakaoId: `${parsed.kakaoId}`,
      name: typeof parsed?.name === 'string' ? parsed.name : undefined,
    };
  } catch {
    return null;
  }
};

const savePendingSignupPayload = (code: string, payload: KakaoEmailRequiredPayload) => {
  sessionStorage.setItem(getPendingSignupStorageKey(code), JSON.stringify(payload));
};

const clearPendingSignupPayload = (code: string) => {
  if (!code) {
    return;
  }

  sessionStorage.removeItem(getPendingSignupStorageKey(code));
};

export const KakaoCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login, completeKakaoSignup } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'processing' | 'email-required' | 'error'>('processing');
  const [pendingSignup, setPendingSignup] = useState<KakaoEmailRequiredPayload | null>(null);

  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [completingSignup, setCompletingSignup] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailInfo, setEmailInfo] = useState<string | null>(null);
  const [emailResendCountdown, setEmailResendCountdown] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasHandledCallbackRef = useRef(false);
  const memoHandledRef = useRef(false);
  const [memoStep, setMemoStep] = useState<'processing' | 'done' | 'error'>('processing');
  const [memoError, setMemoError] = useState<string | null>(null);

  const callbackCode = searchParams.get('code') || '';
  const callbackState = searchParams.get('state') || '';
  const isKakaoMemoFlow = callbackState.startsWith(KAKAO_MEMO_STATE_PREFIX);

  const clearResendTimer = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
  };

  const startResendCountdown = () => {
    clearResendTimer();
    setEmailResendCountdown(60);
    resendTimerRef.current = setInterval(() => {
      setEmailResendCountdown((prev) => {
        if (prev <= 1) {
          clearResendTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      clearResendTimer();
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const redirectToLoginWithError = (message: string, delayMs: number = 2000) => {
    sessionStorage.setItem('socialLoginError', message);
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
    redirectTimerRef.current = setTimeout(() => {
      navigate(`/login?error=${encodeURIComponent(message)}`, { replace: true });
    }, delayMs);
  };

  const redirectAfterLogin = () => {
    const redirectUrl = sessionStorage.getItem('kakaoLoginRedirect') || '/dashboard';
    navigate(redirectUrl, { replace: true });
    // navigate 후 sessionStorage 제거
    if (redirectUrl !== '/dashboard') {
      sessionStorage.removeItem('kakaoLoginRedirect');
    }
  };

  const handleRetryWithEmailConsent = () => {
    const redirectTarget =
      sessionStorage.getItem('kakaoLoginRedirect') || sessionStorage.getItem('redirectAfterLogin') || '/dashboard';
    sessionStorage.setItem('kakaoLoginRedirect', redirectTarget);

    if (callbackCode) {
      clearPendingSignupPayload(callbackCode);
      sessionStorage.removeItem(`oauth:kakao:processed:${callbackCode}`);
      processedKakaoCodes.delete(callbackCode);
    }

    setEmailError(null);
    setError(null);
    window.location.href = buildKakaoAuthorizeUrl({ prompt: 'consent' });
  };

  const handleGoToLogin = () => {
    if (callbackCode) {
      clearPendingSignupPayload(callbackCode);
    }
    sessionStorage.removeItem('kakaoLoginRedirect');
    navigate('/login', { replace: true });
  };

  const handleSendEmailVerification = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setEmailError('이메일을 입력해주세요.');
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setEmailSending(true);
    setEmailError(null);
    setEmailInfo(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/email-verification/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setEmailError(data.message || '인증번호 발송에 실패했습니다.');
        return;
      }

      setEmailCodeSent(true);
      setEmailCode('');
      setEmailVerified(false);
      setEmailInfo('인증번호를 발송했습니다. 메일을 확인해주세요.');
      startResendCountdown();
    } catch {
      setEmailError('인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const normalizedEmail = email.trim();
    const normalizedCode = emailCode.trim();

    if (!normalizedCode) {
      setEmailError('인증번호를 입력해주세요.');
      return;
    }

    setEmailVerifying(true);
    setEmailError(null);
    setEmailInfo(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/email-verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, code: normalizedCode }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setEmailError(data.message || '인증번호 확인에 실패했습니다.');
        return;
      }

      setEmailVerified(true);
      setEmailInfo('이메일 인증이 완료되었습니다. 회원가입을 완료해주세요.');
    } catch {
      setEmailError('인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setEmailVerifying(false);
    }
  };

  const handleCompleteSignup = async () => {
    if (!pendingSignup?.kakaoId) {
      setEmailError('카카오 계정 식별값이 없어 가입을 완료할 수 없습니다. 다시 시도해주세요.');
      return;
    }

    if (!emailVerified) {
      setEmailError('이메일 인증을 먼저 완료해주세요.');
      return;
    }

    setCompletingSignup(true);
    setEmailError(null);
    setEmailInfo(null);

    try {
      await completeKakaoSignup({
        kakaoId: pendingSignup.kakaoId,
        email: email.trim(),
        name: pendingSignup.name,
      });

      if (callbackCode) {
        clearPendingSignupPayload(callbackCode);
      }

      redirectAfterLogin();
    } catch (err: any) {
      setEmailError(err.message || '카카오 회원가입 완료 처리 중 오류가 발생했습니다.');
    } finally {
      setCompletingSignup(false);
    }
  };

  // 카카오톡 관리자 알림 최초 연동 흐름 - 일반 로그인 처리(아래 effect)로 넘어가지
  // 않도록 완전히 분리된 별도 effect로 처리한다.
  useEffect(() => {
    if (!isKakaoMemoFlow) {
      return;
    }
    if (memoHandledRef.current) {
      return;
    }
    memoHandledRef.current = true;

    if (!callbackCode) {
      setMemoStep('error');
      setMemoError('인가 코드가 없습니다.');
      return;
    }

    adminKakaoMemoApi
      .submitCallback(callbackCode, callbackState)
      .then(() => setMemoStep('done'))
      .catch((err: any) => {
        setMemoStep('error');
        setMemoError(err?.response?.data?.message || err?.message || '연동 처리 중 오류가 발생했습니다.');
      });
  }, [isKakaoMemoFlow, callbackCode, callbackState]);

  useEffect(() => {
    if (isKakaoMemoFlow) {
      return;
    }

    const code = searchParams.get('code');

    if (!code) {
      const errorMsg = '인증 코드가 없습니다.';
      setError(errorMsg);
      setStep('error');
      redirectToLoginWithError(errorMsg);
      return;
    }

    const restoredPendingPayload = readPendingSignupPayload(code);
    if (restoredPendingPayload) {
      setPendingSignup(restoredPendingPayload);
      setStep('email-required');
      setError('카카오 계정에서 이메일을 제공받지 못했습니다. 이메일 인증 후 가입을 완료해주세요.');
      return;
    }

    if (hasHandledCallbackRef.current) {
      return;
    }
    hasHandledCallbackRef.current = true;

    const processedKey = `oauth:kakao:processed:${code}`;
    const alreadyProcessed = processedKakaoCodes.has(code) || sessionStorage.getItem(processedKey) === '1';

    if (alreadyProcessed) {
      const hasAuthToken = !!(localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));
      if (hasAuthToken) {
        redirectAfterLogin();
        return;
      }

      setStep('error');
      const errorMsg = '카카오 로그인 상태를 확인할 수 없습니다. 다시 시도해주세요.';
      setError(errorMsg);
      redirectToLoginWithError(errorMsg);
      return;
    }

    processedKakaoCodes.add(code);
    sessionStorage.setItem(processedKey, '1');

    const handleKakaoCallback = async () => {
      try {
        setStep('processing');
        setError(null);

        // 회원가입 컨텍스트에서 저장된 동의 정보 읽기
        const isSignupContext = sessionStorage.getItem('kakaoSignupContext') === 'true';
        sessionStorage.removeItem('kakaoSignupContext');
        let consentData: { agreeTerms?: boolean; agreePrivacy?: boolean; agreeMarketing?: boolean } | undefined;
        if (isSignupContext) {
          const raw = sessionStorage.getItem('socialPreOAuthConsent');
          sessionStorage.removeItem('socialPreOAuthConsent');
          if (raw) {
            try { consentData = JSON.parse(raw); } catch { /* ignore */ }
          }
        }

        await login(code, '', false, 'kakao', consentData);
        redirectAfterLogin();
      } catch (err: any) {
        // 신규 유저: 회원가입 페이지에서 동의 후 카카오 소셜 가입을 진행하도록 안내
        if (err.code === 'SOCIAL_NEW_USER' || err.data?.errorType === 'SOCIAL_NEW_USER') {
          navigate('/signup', { state: { autoOpenConsent: 'kakao' }, replace: true });
          return;
        }
        // 승인 대기 신청 완료 (이미 가입된 유저)
        if (
          err.code === 'SOCIAL_SIGNUP_PENDING' ||
          err.message?.startsWith('SOCIAL_SIGNUP_PENDING|') ||
          err.data?.errorType === 'SOCIAL_SIGNUP_PENDING'
        ) {
          const rawMsg = err.message || '';
          const userMsg = rawMsg.startsWith('SOCIAL_SIGNUP_PENDING|')
            ? rawMsg.substring('SOCIAL_SIGNUP_PENDING|'.length)
            : '회원가입 신청이 완료되었습니다. 관리자 승인 후 이용 가능합니다.';
          const pendingEmail = err.data?.email || err.data?.data?.email;
          navigate('/pending-approval', { state: { message: userMsg, email: pendingEmail }, replace: true });
          return;
        }
        if (isKakaoEmailRequiredError(err)) {
          const payload = extractKakaoEmailRequiredPayload(err);

          if (!payload) {
            setStep('error');
            const errorMsg = '카카오 계정 식별 정보가 누락되어 가입을 완료할 수 없습니다. 다시 시도해주세요.';
            setError(errorMsg);
            redirectToLoginWithError(errorMsg);
            return;
          }

          savePendingSignupPayload(code, payload);
          setPendingSignup(payload);
          setStep('email-required');
          setError('카카오 계정에서 이메일을 제공받지 못했습니다. 이메일 인증 후 가입을 완료해주세요.');
          return;
        }

        // ✅ 이슈 #4: OAuth code 재사용 에러 처리 (B/E OAUTH_CODE_ALREADY_USED 반환 시)
        if (err.code === 'OAUTH_CODE_ALREADY_USED' || err.data?.errorType === 'OAUTH_CODE_ALREADY_USED') {
          redirectToLoginWithError('이미 처리된 로그인 요청입니다. 다시 시도해주세요.', 0);
          return;
        }

        const errorMsg = err.message || '카카오 로그인 처리 중 오류가 발생했습니다';
        setStep('error');
        setError(errorMsg);
        redirectToLoginWithError(errorMsg);
      }
    };

    handleKakaoCallback();
  }, [searchParams, login, navigate]);

  if (isKakaoMemoFlow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          {memoStep === 'processing' && (
            <>
              <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
              <p className="text-gray-600">카카오톡 관리자 알림 연동 처리 중...</p>
            </>
          )}
          {memoStep === 'done' && (
            <p className="text-green-600 text-lg">
              카카오톡 관리자 알림 연동이 완료되었습니다. 이 창을 닫으셔도 됩니다.
            </p>
          )}
          {memoStep === 'error' && (
            <div className="text-red-600">
              <p className="mb-2">연동 실패: {memoError}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 'email-required' && pendingSignup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>카카오 이메일 입력 필요</CardTitle>
            <CardDescription>
              카카오 계정에서 이메일을 제공받지 못했습니다. 이메일 인증 후 가입을 완료해주세요.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">{error}</AlertDescription>
              </Alert>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">이메일</label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailVerified(false);
                    setEmailCodeSent(false);
                    setEmailCode('');
                    setEmailError(null);
                    setEmailInfo(null);
                    clearResendTimer();
                    setEmailResendCountdown(0);
                  }}
                  placeholder="이메일을 입력하세요"
                  disabled={emailSending || emailVerifying || completingSignup}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendEmailVerification}
                  disabled={
                    emailSending ||
                    emailVerifying ||
                    completingSignup ||
                    !email.trim() ||
                    emailResendCountdown > 0
                  }
                  className="whitespace-nowrap"
                >
                  {emailSending ? (
                    <Spinner size="sm" />
                  ) : emailCodeSent ? (
                    emailResendCountdown > 0 ? `재발송 (${emailResendCountdown}s)` : '재발송'
                  ) : (
                    '인증번호 발송'
                  )}
                </Button>
              </div>
            </div>

            {emailCodeSent && !emailVerified && (
              <div className="space-y-2">
                <label className="block text-sm font-medium">인증번호</label>
                <div className="flex gap-2">
                  <Input
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                    placeholder="인증번호 6자리 입력"
                    maxLength={6}
                    disabled={emailVerifying || completingSignup}
                  />
                  <Button
                    type="button"
                    onClick={handleVerifyEmailCode}
                    disabled={emailVerifying || completingSignup || !emailCode.trim()}
                    className="whitespace-nowrap"
                  >
                    {emailVerifying ? <Spinner size="sm" /> : '확인'}
                  </Button>
                </div>
              </div>
            )}

            {emailVerified && (
              <p className="text-sm text-green-600">✓ 이메일 인증이 완료되었습니다.</p>
            )}

            {emailInfo && <p className="text-sm text-blue-700">{emailInfo}</p>}
            {emailError && <p className="text-sm text-red-600">{emailError}</p>}

            <Button
              type="button"
              className="w-full"
              onClick={handleCompleteSignup}
              disabled={!emailVerified || completingSignup}
            >
              {completingSignup ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  가입 처리 중...
                </>
              ) : (
                '카카오 가입 완료'
              )}
            </Button>

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="w-full" onClick={handleRetryWithEmailConsent}>
                카카오 동의 다시 요청
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleGoToLogin}>
                로그인으로 이동
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          {step === 'error' && error ? (
            <div className="text-red-600">
              <p className="mb-4">{error}</p>
              <p className="text-sm text-gray-600">잠시 후 로그인 페이지로 이동합니다...</p>
            </div>
          ) : (
            <div>
              <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
              <p className="text-gray-600">카카오 로그인 처리 중...</p>
            </div>
          )}
        </div>
      </div>

      {/* 신규 가입 동의 모달 제거 (카카오 버튼 비활성화 중) */}
    </>
  );
};
